
import React from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, ArrowUpRight, ArrowDownRight, RefreshCw, AlertTriangle } from "lucide-react";
import { TypeTooltip } from "./TypeTooltip";
import { CategoryTooltip } from "./CategoryTooltip";
import { ExpirySticker } from "./ExpirySticker";
import { format } from "date-fns";
import { Contract } from "@/lib/types";
import { importanceBadges } from "./types";
import { useImportanceColors } from "@/hooks/useImportanceColors";
import { getDirectionIcon } from "@/components/contract-types/ContractDirectionIcons";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FeatureGuard } from "@/components/common/FeatureGuard";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";

interface ContractWithDetails extends Contract {
  importanceColor?: string;
  convertedAmount?: number | null;
  conversionRate?: number;
  isConverting?: boolean;
  isGroupHeader?: boolean;
  convertedCurrency?: {
    code: string;
    symbol: string;
  } | null;
}

interface ContractCardViewProps {
  contracts: ContractWithDetails[];
  onDeleteContract: (id: string) => void;
  isColumnSelected: (id: string) => boolean;
  selectedColumns: string[];
  defaultCurrencySymbol?: string;
  canUseGroupedContracts?: boolean;
}

export function ContractCardView({
  contracts,
  onDeleteContract,
  isColumnSelected,
  selectedColumns,
  defaultCurrencySymbol,
  canUseGroupedContracts = false
}: ContractCardViewProps) {
  const { getImportanceColor } = useImportanceColors();
  
  // Filter out group headers if user doesn't have grouped contracts feature
  const filteredContracts = canUseGroupedContracts 
    ? contracts 
    : contracts.filter(contract => !contract.isGroupHeader);
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {filteredContracts.map((contract) => {
        const isIncome = contract.type?.direction === "incoming";
        
        return (
          <Card key={contract.id} className="overflow-hidden border dark:border-gray-800">
            <CardContent className="p-0">
              <div className="p-4 border-b dark:border-gray-800 bg-muted/40">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-lg truncate">{contract.supplierName}</h3>

                  <div className="flex flex-col items-end gap-1">
                    {isColumnSelected("expiryStatus") && (
                      <ExpirySticker contract={contract} />
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {isColumnSelected("importance") && (
                    (() => {
                      const { className, style } = getImportanceColor(contract.importance);
                      return (
                        <Badge 
                          variant={importanceBadges[contract.importance]?.variant || "outline"}
                          className={className}
                          style={style}
                        >
                          {importanceBadges[contract.importance]?.label || `Importance ${contract.importance}`}
                        </Badge>
                      );
                    })()
                  )}

                  {isColumnSelected("type") && contract.type && (
                    <TypeTooltip type={contract.type}>
                      <Badge variant="outline" className="border-blue-200 text-blue-700 dark:border-blue-800 dark:text-blue-300 flex items-center gap-1.5">
                        {contract.type.direction && getDirectionIcon(contract.type.direction, "h-3.5 w-3.5 mr-1")}
                        {contract.type.name}
                      </Badge>
                    </TypeTooltip>
                  )}

                  {isColumnSelected("category") && contract.category && (
                    <CategoryTooltip category={contract.category}>
                      <Badge variant="outline" className="border-green-200 text-green-700 dark:border-green-800 dark:text-green-300">
                        {contract.category.name}
                      </Badge>
                    </CategoryTooltip>
                  )}
                </div>
              </div>

              <div className="p-4 space-y-3">
                {isColumnSelected("expiryDate") && contract.expiryDate && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Expiry Date:</span>
                    <span className="font-medium">{format(new Date(contract.expiryDate), "PP")}</span>
                  </div>
                )}

                {isColumnSelected("signedDate") && contract.signedDate && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Signed Date:</span>
                    <span className="font-medium">{format(new Date(contract.signedDate), "PP")}</span>
                  </div>
                )}

                {isColumnSelected("expectedCost") && contract.expectedCost && (
                  <div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Value:</span>
                      <div className="flex items-center gap-1">
                        {isIncome ? (
                          <ArrowUpRight className="h-4 w-4 text-green-500" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4 text-red-500" />
                        )}
                        <span className={`font-medium ${isIncome ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                          {contract.currency?.symbol}{" "}
                          {Number(contract.expectedCost).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                    
                    {contract.convertedAmount !== null && contract.convertedCurrency && (
                      <FeatureGuard feature="currency_conversion">
                        <div className="flex justify-end mt-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1 text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 rounded-full cursor-help max-w-fit">
                                  <RefreshCw className="h-3 w-3 text-blue-500" />
                                  <span className="text-blue-700 dark:text-blue-300">
                                    {contract.convertedAmount.toLocaleString(undefined, {maximumFractionDigits: 2})} 
                                    {contract.convertedCurrency.symbol}
                                  </span>
                                  <AlertTriangle className="h-3 w-3 text-amber-500 ml-0.5 opacity-70" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="bg-black text-white border-gray-800 p-3 max-w-[300px]">
                                <div className="space-y-2">
                                  <p>Converted at rate: 1 {contract.currency?.code} = {contract.conversionRate?.toFixed(4)} {contract.convertedCurrency.code}</p>
                                  <p className="text-amber-300 text-xs flex items-start gap-1.5">
                                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                    <span>Currency conversions are estimates and may not reflect exact rates at the time of contract payment.</span>
                                  </p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </FeatureGuard>
                    )}
                    
                    {contract.isConverting && (
                      <FeatureGuard feature="currency_conversion">
                        <div className="flex justify-end mt-1">
                          <span className="text-xs text-gray-400">converting...</span>
                        </div>
                      </FeatureGuard>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="border-t dark:border-gray-800 p-3 bg-muted/20">
              <div className="flex justify-between w-full items-center">
                {isColumnSelected("currency") && contract.currency && (
                  <Badge variant="outline" className="text-xs">
                    {contract.currency.code}
                  </Badge>
                )}

                {isColumnSelected("actions") && (
                  <div className="flex items-center space-x-2 ml-auto">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => onDeleteContract(contract.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
}
